import { useState } from 'react';
import EmbeddedCardLayout from '../../../components/embedded/CardLayout';
import EmbeddedQrCodeCard from '../../../components/embedded/QrCodeCard';
import EmbeddedCaption from '../../../components/embedded/Caption';
import EmbeddedNeedHelpSection from '../../../components/embedded/NeedHelpSection';
import { detectOs } from '../../../utils/platform';
import { useQrOnScreen } from '../../../hooks/useQrOnScreen';
import { type AgentStartPageProps } from '../../../chulak/ChulakSession';
import EmbeddedDetailsScreen from '../shared/DetailsScreen';
import EmbeddedStatusScreen from '../shared/StatusScreen';
import EmbeddedUnsupportedScreen from '../shared/UnsupportedScreen';
import { useAnotherApp, useEmbeddedCopy } from '../../../hooks/useEmbeddedCopy';
import { type ViewTable } from '../../types';
import { appLinkFor, openLink } from '../../../utils/appLinks';
import EmbeddedMissingInstallScreen from '../shared/MissingInstallScreen';
import EmbeddedNotOpenedScreen from './NotOpenedScreen';
import EmbeddedPhoneInstallScreen from './PhoneInstallScreen';

type View =
  'launch' | 'opening' | 'notOpened' | 'install' | 'missing' | 'qr' | 'details';

/**
 * Open Yivi (phones): replaces the QR screen. The QR code is two taps away,
 * for a wallet on another device.
 */
export default function EmbeddedMobileAgentStartPage({
  tags,
  app,
  onBack,
}: AgentStartPageProps) {
  const { t, pick, issuance } = useEmbeddedCopy();
  const qr = useQrOnScreen();
  // After "Refresh QR" the fresh session opens on the QR code again.
  const [view, setViewState] = useState<View>(qr.shown ? 'qr' : 'launch');
  const setView = (next: View) => {
    setViewState(next);
    qr.setShown(next === 'qr');
  };
  const anotherApp = useAnotherApp(onBack);
  const appName = app?.name ?? '';
  const appLink = appLinkFor(tags, detectOs());

  if (tags.type === 'Redirect') {
    return <EmbeddedUnsupportedScreen app={app} onBack={onBack} />;
  }

  const openApp = () => {
    if (!appLink) {
      setView('qr');
      return;
    }
    openLink(appLink);
    setView('opening');
  };
  const toLaunch = () => setView('launch');

  const views: ViewTable<View> = {
    launch: () => (
      <EmbeddedStatusScreen
        visual="app"
        app={app}
        title={t(
          pick('embedded.launch.title', 'embedded.launch.titleIssuance'),
          {
            app: appName,
          },
        )}
        sub={t(pick('embedded.launch.sub', 'embedded.launch.subIssuance'), {
          app: appName,
        })}
        primary={{
          label: t('embedded.launch.open', { app: appName }),
          onClick: openApp,
        }}
        secondary={anotherApp}
        links={[
          {
            label: t('embedded.qr.noApp', { app: appName }),
            onClick: () => setView('install'),
          },
          {
            label: t(pick('embedded.qr.whatShared', 'embedded.qr.whatAdded')),
            onClick: () => setView('details'),
          },
        ]}
      />
    ),
    opening: () => (
      <EmbeddedStatusScreen
        visual="app"
        app={app}
        badge="phone"
        title={t('embedded.waiting.title', { app: appName })}
        sub={t(pick('embedded.opening.sub', 'embedded.opening.subIssuance'))}
        secondary={{ label: t('embedded.cancel'), onClick: toLaunch }}
        links={[
          {
            label: t('embedded.opening.help', { app: appName }),
            onClick: () => setView('notOpened'),
          },
        ]}
      />
    ),
    notOpened: () => (
      <EmbeddedNotOpenedScreen
        app={app}
        onInstall={() => setView('install')}
        onBack={() => setView('opening')}
      />
    ),
    install: () => (
      <EmbeddedPhoneInstallScreen
        app={app}
        onOpenApp={openApp}
        onScanElsewhere={() => setView('qr')}
        onNoStoreLink={() => setView('missing')}
        onBack={onBack}
      />
    ),
    missing: () => (
      <EmbeddedMissingInstallScreen
        app={app}
        onInstalled={toLaunch}
        onBack={onBack}
      />
    ),
    qr: () => (
      <EmbeddedCardLayout
        secondary={anotherApp}
        help={
          <EmbeddedNeedHelpSection
            links={[
              {
                label: t('embedded.phoneQr.openHere', { app: appName }),
                onClick: toLaunch,
              },
            ]}
          />
        }
      >
        <EmbeddedQrCodeCard
          data={tags.type === 'Qr' ? tags.qr.content : ''}
          logo={app?.logo}
          label={t('embedded.qr.label', { app: appName })}
        />
        <EmbeddedCaption
          text={t(
            issuance ? 'embedded.qr.captionIssuance' : 'embedded.qr.caption',
          )}
          bold={appName}
        />
      </EmbeddedCardLayout>
    ),
    details: () => (
      <EmbeddedDetailsScreen
        back={{ label: t('embedded.back'), onClick: toLaunch }}
      />
    ),
  };

  return views[view]();
}
